import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import usePersistentPayerCode from "../../hooks/usePersistentPayerCode";

const ProviderDirectory = () => {
  usePersistentPayerCode();

  return (
    <div className="page-layout api-layout overflow-x-hidden!">
      <div className="flex flex-col gap-6">
        {/* Hero */}
        <div className="relative rounded-2xl p-6 md:p-8 text-white bg-[linear-gradient(120deg,#041e79_0%,#0331a4_45%,#8c32b7_100%)] shadow-[0_12px_30px_rgba(4,30,121,0.18)]">
          <div className="pointer-events-none absolute -top-16 -right-16 h-56 w-56 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.18)_0%,rgba(255,255,255,0)_70%)]" />
          <div className="relative flex flex-col gap-6 justify-between md:flex-row md:items-center">
            <div className="flex flex-row items-center gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
                <LocalHospitalOutlinedIcon fontSize="large" />
              </div>
              <div>
                <p className="m-0 text-xs font-semibold uppercase opacity-85">
                  Provider Directory API
                </p>
                <h2 className="m-0 mt-1 text-2xl font-bold leading-tight text-white normal-case">
                  Developer Guide
                </h2>
              </div>
            </div>

            <div className="flex h-6 items-center rounded-full border border-white/25 bg-white/15 px-2.5 text-xs font-semibold text-white w-max">
              FHIR ENABLED
            </div>
          </div>
        </div>

        {/* Overview + Description */}
        <div className="rounded-2xl border border-[#e3e7f3] bg-white">
          <div className="p-5 md:p-7">
            <div className="grid grid-cols-1 gap-8 xl:grid-cols-[5fr_7fr]">
              <div>
                <div className="mb-3 flex flex-row items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#f7e6ff] text-(--moderatevioletcustom)">
                    <MenuBookOutlinedIcon fontSize="small" />
                  </div>
                  <h3 className="m-0 text-lg font-semibold normal-case">
                    Product Overview
                  </h3>
                </div>
                <p id="desc" className="m-0 text-sm">
                  Provider-related resources include insurance plan information,
                  locations, healthcare services, and networks required to
                  deliver provider directory information. Provider directory
                  information also includes pharmacies.
                </p>

                {/* Compliance banner */}
                <div className="mt-6 flex flex-col items-start gap-3 rounded-xl border border-[#e3e7f3] bg-[linear-gradient(120deg,#ffffff_0%,#e5ebff_100%)] p-4">
                  <div className="flex flex-row items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-(--darkblue) text-white shadow-[0_4px_10px_rgba(4,30,121,0.25)]">
                      <GavelOutlinedIcon fontSize="small" />
                    </div>
                    <div>
                      <p className="m-0 text-[10px] font-semibold uppercase tracking-widest">
                        Regulatory Mandate
                      </p>
                      <p className="m-0 mt-0.5 text-sm font-semibold">
                        CMS Interoperability and Patient Access Final Rule
                      </p>
                    </div>
                  </div>
                  <span className="inline-flex w-max items-center gap-1.5 rounded-full bg-(--darkblue) px-3 py-1.5 text-xs font-bold text-white shadow-[0_4px_10px_rgba(4,30,121,0.25)]">
                    <span className="h-1.5 w-1.5 rounded-full bg-white" />
                    CMS-9115-F
                  </span>
                </div>
              </div>

              <div>
                <div className="mb-3 flex flex-row items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#e5ebff] text-(--darkblue)">
                    <VerifiedOutlinedIcon fontSize="small" />
                  </div>
                  <h3 className="m-0 text-lg font-semibold normal-case">
                    Product Description
                  </h3>
                </div>

                <div className="flex flex-col gap-3">
                  <p className="m-0 text-sm">
                    As part of the CMS Interoperability and Patient Access final
                    rule (CMS-9115-F), payers must make provider information
                    available. These APIs are required to retrieve the provider
                    information directly.
                  </p>
                  <p className="m-0 text-sm">
                    There are nine provider resources that conform to the HL7
                    FHIR standards. These resources provide information on
                    providers and pharmacies within the specific payer&apos;s
                    network. Note that four of these resources also appear in
                    the clinical section.
                  </p>
                  <p className="m-0 text-sm">
                    More details on this API can be found in the Capability
                    Statement API.
                  </p>
                  <p className="m-0 text-sm">
                    APIs in this category follow the PDEX Payer Network
                    Implementation Guide v1.0.0 found at
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Implementation Guide CTA */}
        <div className="rounded-2xl border border-[#e3e7f3] bg-[linear-gradient(120deg,#ffffff_0%,#f7e6ff_60%,#e5ebff_100%)]">
          <div className="p-5 md:p-6">
            <div className="flex flex-col gap-4 justify-between md:flex-row md:items-center">
              <div className="flex flex-row items-center gap-4">
                <div className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-lg bg-white text-(--moderatevioletcustom) shadow-[0_6px_16px_rgba(140,50,183,0.15)]">
                  <MenuBookOutlinedIcon />
                </div>
                <div>
                  <p className="m-0 text-xs font-semibold tracking-widest">
                    Implementation Guide
                  </p>
                  <h3 className="m-0 mt-0.5 text-base font-semibold normal-case">
                    PDEX Payer Network IG v1.2.0
                  </h3>
                  <p className="m-0 mt-1 text-sm">
                    APIs in this category follow the PDEX Payer Network
                    Implementation Guide.
                  </p>
                </div>
              </div>

              <a
                href="https://hl7.org/fhir/us/davinci-pdex-plan-net/STU1.2"
                target="_blank"
                rel="noopener noreferrer"
                className="no-underline"
              >
                <span className="inline-flex items-center gap-2 rounded-lg bg-(--darkblue) px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-(--moderatevioletcustom) hover:shadow-[0_8px_18px_rgba(140,50,183,0.3)]">
                  View Guide
                  <LaunchOutlinedIcon fontSize="small" />
                </span>
              </a>
            </div>

            <div className="mt-5 break-all rounded-lg border border-dashed border-(--moderatevioletcustom)/35 bg-white p-3 text-sm">
              <a
                href="https://hl7.org/fhir/us/davinci-pdex-plan-net/STU1.2"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-(--moderatevioletcustom) underline!"
              >
                https://hl7.org/fhir/us/davinci-pdex-plan-net/STU1.2
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderDirectory;
